import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Auth() {
  const [params] = useSearchParams();
  const [isRegister, setIsRegister] = useState(params.get("mode") === "register");
  const [forgot, setForgot] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [ragioneSociale, setRagioneSociale] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { user, login, register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/app");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (forgot) {
        await api.post("/auth/forgot-password", { email });
        setForgotSent(true);
      } else if (isRegister) {
        await register(email, password, ragioneSociale);
        toast.success("Account creato con successo");
        navigate("/app");
      } else {
        await login(email, password);
        toast.success("Bentornato!");
        navigate("/app");
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden flex-col justify-between overflow-hidden bg-slate-900 p-12 text-white lg:flex">
        <div className="grid-backdrop absolute inset-0 opacity-30" />
        <Link to="/" className="relative flex items-center gap-2">
          <img src="/logo-atb.png" alt="A Tutto Bando" className="h-9 w-9 rounded-xl" />
          <span className="font-display text-lg font-bold">A Tutto Bando</span>
        </Link>
        <div className="relative">
          <h2 className="font-display text-4xl font-extrabold leading-tight">
            I bandi giusti,
            <br />trovati dall'AI.
          </h2>
          <p className="mt-4 max-w-md text-slate-300">
            Registra la tua azienda una volta e ottieni una lista personalizzata di
            finanziamenti, agevolazioni e contributi a fondo perduto.
          </p>
        </div>
        <p className="relative text-sm text-slate-500">
          © 2026 A Tutto Bando
        </p>
      </div>

      <div className="flex items-center justify-center px-4 py-12 sm:px-6">
        <div className="w-full max-w-md">
          <div className="mb-8 lg:hidden">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo-atb.png" alt="A Tutto Bando" className="h-9 w-9 rounded-xl" />
              <span className="font-display text-lg font-bold">A Tutto Bando</span>
            </Link>
          </div>

          <h1 className="font-display text-3xl font-bold tracking-tight">
            {forgot ? "Password dimenticata" : isRegister ? "Crea il tuo account" : "Accedi"}
          </h1>
          <p className="mt-2 text-slate-500">
            {forgot
              ? "Inserisci la tua email: ti invieremo un link per reimpostare la password."
              : isRegister
                ? "Bastano pochi secondi per iniziare a cercare i tuoi bandi."
                : "Inserisci le tue credenziali per continuare."}
          </p>

          {forgot && forgotSent ? (
            <div className="mt-8">
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700" data-testid="forgot-sent-message">
                Se l'email è registrata, riceverai a breve un link per reimpostare la password. Controlla anche lo spam.
              </div>
              <button
                type="button"
                onClick={() => { setForgot(false); setForgotSent(false); setError(""); }}
                className="mt-6 text-sm font-semibold text-indigo-600 hover:underline"
                data-testid="back-to-login-button"
              >
                ← Torna all'accesso
              </button>
            </div>
          ) : (
          <form onSubmit={submit} className="mt-8 space-y-4">
            {!forgot && isRegister && (
              <div className="space-y-1.5">
                <Label htmlFor="rs">Ragione Sociale</Label>
                <Input
                  id="rs"
                  data-testid="auth-company-input"
                  placeholder="Es. Rossi Impianti SRL"
                  value={ragioneSociale}
                  onChange={(e) => setRagioneSociale(e.target.value)}
                  required
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="auth-email-input"
                placeholder="nome@azienda.it"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            {!forgot && (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="pw">Password</Label>
                  {!isRegister && (
                    <button
                      type="button"
                      data-testid="forgot-password-link"
                      onClick={() => { setForgot(true); setError(""); }}
                      className="text-xs font-semibold text-indigo-600 hover:underline"
                    >
                      Password dimenticata?
                    </button>
                  )}
                </div>
                <Input
                  id="pw"
                  type="password"
                  data-testid="auth-password-input"
                  placeholder="Almeno 6 caratteri"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
            )}

            {error && (
              <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600" data-testid="auth-error">
                {error}
              </p>
            )}

            <Button type="submit" className="h-11 w-full gap-2 text-base" disabled={loading} data-testid="auth-submit-button">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {forgot ? "Invia link di reset" : isRegister ? "Crea account" : "Accedi"}
            </Button>

            {forgot && (
              <button
                type="button"
                onClick={() => { setForgot(false); setError(""); }}
                className="w-full text-center text-sm font-semibold text-indigo-600 hover:underline"
                data-testid="back-to-login-button"
              >
                ← Torna all'accesso
              </button>
            )}
          </form>
          )}

          {!forgot && (
          <p className="mt-6 text-center text-sm text-slate-500">
            {isRegister ? "Hai già un account?" : "Non hai un account?"}{" "}
            <button
              type="button"
              data-testid="auth-toggle-mode-button"
              onClick={() => { setIsRegister(!isRegister); setError(""); }}
              className="font-semibold text-indigo-600 hover:underline"
            >
              {isRegister ? "Accedi" : "Registrati"}
            </button>
          </p>
          )}
        </div>
      </div>
    </div>
  );
}
